import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import {
  deviceRecente,
  usePrintAgentDevices,
  useUltimaVersaoDoApp,
  versaoMenor,
  type PrintAgentDevice,
} from "@/lib/printAgentDevices";

export { deviceRecente, usePrintAgentDevices, type PrintAgentDevice };
import {
  Loader2,
  Copy,
  Check,
  Server,
  AlertTriangle,
  RotateCcw,
  Download,
  Smartphone,
  KeyRound,
} from "lucide-react";

interface AgentToken {
  id: string;
  label: string;
  refreshTokenPlain: string | null;
  hostname: string | null;
  os: string | null;
  agentVersion: string | null;
  createdAt: string;
  lastSeenAt: string | null;
}

interface TokensResponse {
  token: AgentToken;
  lastPrintAgentPingAt: string | null;
}

interface FailedQueueItem {
  id: string;
  orderId: string;
  reason: string;
  attempts: number;
  lastError: string | null;
  createdAt: string;
}

function nomeDoAparelho(d: PrintAgentDevice): string {
  const tipo = (d.os ?? "").startsWith("android") ? "celular" : "computador";
  return d.hostname ? `${tipo} ${d.hostname}` : tipo;
}

function formatDate(d: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

function isAgentOnline(lastPing: string | null): boolean {
  if (!lastPing) return false;
  return Date.now() - new Date(lastPing).getTime() < 90_000;
}

export function PrintAgentSection() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery<TokensResponse>({
    queryKey: ["print-agent-tokens"],
    queryFn: () => customFetch<TokensResponse>("/api/settings/print-agent/tokens"),
    refetchInterval: 30_000,
  });

  const token = data?.token;
  const online = isAgentOnline(data?.lastPrintAgentPingAt ?? null);

  const { data: devices = [] } = usePrintAgentDevices();
  // Versão mais recente do app do Windows (o Worker resolve a release do GitHub).
  const { data: ultima } = useUltimaVersaoDoApp("windows");
  const semImpressora = devices.filter((d) => d.semImpressora && deviceRecente(d, 30));
  // Desatualizado: só app do Windows. O build pra Windows 7/8 ("-win8") fica
  // de fora — a versão do botão não roda nesses sistemas.
  const desatualizados = ultima?.version
    ? devices.filter(
        (d) =>
          d.os === "win32" &&
          d.agentVersion &&
          !d.agentVersion.includes("win8") &&
          versaoMenor(d.agentVersion, ultima.version),
      )
    : [];

  async function copyToken() {
    if (!token?.refreshTokenPlain) return;
    try {
      await navigator.clipboard.writeText(token.refreshTokenPlain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Não foi possível copiar", variant: "destructive" });
    }
  }

  const { data: failedItems = [] } = useQuery<FailedQueueItem[]>({
    queryKey: ["print-queue-failed"],
    queryFn: async () => {
      const r = await customFetch<{ items: FailedQueueItem[] }>("/api/print-queue/failed");
      return r.items;
    },
    refetchInterval: 30_000,
  });

  const reprintMutation = useMutation({
    mutationFn: async (id: string) =>
      customFetch(`/api/print-queue/${id}/reprint`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["print-queue-failed"] });
      toast({ title: "Item enviado novamente para a fila" });
    },
    onError: (err) => {
      toast({
        title: "Erro ao reimprimir",
        description: extractApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <div className="space-y-4 pt-6 mt-2 border-t-2 border-border">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-base font-semibold">
            <Server className="w-4 h-4 text-primary" /> Aplicativo de Impressão
          </div>
          <div
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
              online
                ? "bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400"
                : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${online ? "bg-green-500 animate-pulse" : "bg-gray-400"}`}
            />
            {online ? "Online" : "Offline"}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Use o aplicativo desktop (Windows) ou o app Android para imprimir
          cupons sem deixar o navegador aberto. O mesmo código de conexão
          abaixo vale para os dois.
        </p>

        {semImpressora.map((d) => (
          <div
            key={`sem-${d.deviceId}`}
            className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              O aplicativo no {nomeDoAparelho(d)} está aberto, mas sem impressora escolhida. Abra o aplicativo e escolha a impressora.
            </span>
          </div>
        ))}
        {desatualizados.map((d) => (
          <div
            key={`old-${d.deviceId}`}
            className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              O aplicativo no {nomeDoAparelho(d)} está na versão {d.agentVersion}. Baixe a {ultima?.version} no botão abaixo e instale por cima.
            </span>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            asChild
          >
            <a
              href="/download/print-agent"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="w-4 h-4 mr-1" />
              Baixar instalador (Windows)
            </a>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            asChild
          >
            <a
              href="/download/android"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Smartphone className="w-4 h-4 mr-1" />
              Baixar app (Android)
            </a>
          </Button>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        ) : token?.refreshTokenPlain ? (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" />
              Token de conexão (cole no app Windows ou Android)
            </Label>
            <div className="flex items-center gap-2 p-2 rounded-lg border border-border bg-muted/30">
              <code className="flex-1 text-xs font-mono break-all text-foreground">
                {token.refreshTokenPlain}
              </code>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer shrink-0"
                onClick={copyToken}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1 text-green-600" /> Copiado
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" /> Copiar
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {failedItems.length > 0 && (
        <Card className="p-6 space-y-3 mt-6">
          <div className="flex items-center gap-2 text-base font-semibold">
            <AlertTriangle className="w-4 h-4 text-destructive" /> Fila de impressão — falhas
          </div>
          <p className="text-sm text-muted-foreground">
            Itens que falharam após 3 tentativas. Use "Reimprimir" para tentar de novo.
          </p>
          <div className="space-y-2">
            {failedItems.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    Pedido #{it.orderId.slice(-6).toUpperCase()}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    Motivo: {it.lastError || "—"} · {it.attempts} tentativas ·{" "}
                    {formatDate(it.createdAt)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer shrink-0"
                  onClick={() => reprintMutation.mutate(it.id)}
                  disabled={reprintMutation.isPending}
                >
                  <RotateCcw className="w-4 h-4 mr-1" /> Reimprimir
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

    </>
  );
}
